import React, { useMemo } from 'react';

import { axisScale, curve, endLabelMargin, referenceMarks, scaleBounds, valueLabels, xAxisName, xLabelSpacing, xTitleRoom, yAxisName } from '../axisStyle';
import { formatAxis, formatValue, monthOf, pointLabel, unitName } from '../format';
import type { Palette } from '../palette';
import { overrideColor, seriesOverrides } from '../seriesStyle';
import { timeAxis } from '../timeAxis';
import type { Block, Envelope, EnvelopeColumn } from '../types';

import Chart, { type ChartOption } from './Chart';
import type { LegendItem } from './Legend';

type ComboProps = { block: Block; envelope: Envelope; palette: Palette; width?: number };

export function comboOption({ block, envelope, palette, width = 1200 }: ComboProps): ChartOption {
  const overrides = seriesOverrides(block);
  const measures = envelope.columns.filter((column) => column.role === 'measure') as Array<EnvelopeColumn>;
  const marks = (block.marks ?? {}) as Record<string, string>;
  const declared = (Array.isArray(block.right) ? block.right : []) as Array<string>;
  const lower = (measure: EnvelopeColumn, index: number) => declared.includes(measure.name) || (!declared.length && index > 0 && measure.format !== measures[0].format);
  const panels = measures.some(lower) ? 2 : 1;
  const time = timeAxis([...new Set([...envelope.rows, ...Object.values(envelope.compare).flat()].map((row) => String(row.t)))].sort(), block, envelope);
  const days = time.days;
  const byDay = new Map(envelope.rows.map((row) => [String(row.t), row]));
  const markOf = (measure: EnvelopeColumn, index: number) => marks[measure.name] ?? (index === 0 ? 'bar' : 'line');
  const isBar = (measure: EnvelopeColumn, index: number) => markOf(measure, index) === 'bar';
  const lines = measures.filter((measure, index) => !isBar(measure, index) && !overrides[measure.name]?.hidden);
  const labelWidth = lines.length ? Math.min(Math.max(...lines.map((measure) => (overrides[measure.name]?.label ?? measure.label).length)) * 6.4 + 14, (width || 1200) * 0.25) : 12;
  const right = Math.max(12, ...lines.map((measure) => endLabelMargin(days.map((day) => (byDay.get(day)?.[measure.name] as number | null) ?? null), labelWidth, width || 1200)));
  const grids = Array.from({ length: panels }, (_, panel) => ({ left: 4, right, top: panel === 0 ? 16 : '54%', ...(panel === panels - 1 ? { bottom: 4 + xTitleRoom(block) } : { height: '44%' }), containLabel: true }));
  const firstBelow = measures.findIndex((measure, index) => panels === 2 && lower(measure, index));
  const series = measures.flatMap((measure, index) => {
    if (overrides[measure.name]?.hidden) return [];
    const bar = isBar(measure, index);
    const color = overrideColor(overrides[measure.name], palette, palette.series[index % palette.series.length]);
    const panel = panels === 2 && lower(measure, index) ? 1 : 0;
    const label = overrides[measure.name]?.label ?? measure.label;
    const values = days.map((day) => (byDay.get(day)?.[measure.name] as number | null) ?? null);
    const current = {
      id: measure.name,
      name: label,
      type: bar ? 'bar' : 'line',
      xAxisIndex: panel,
      yAxisIndex: panel,
      data: values,
      showSymbol: !bar && values.filter((value) => value !== null).length === 1,
      symbol: 'circle',
      symbolSize: 6,
      barMaxWidth: 18,
      itemStyle: { color, opacity: bar ? 0.85 : 1 },
      lineStyle: { width: 1.75, color },
      labelLayout: { moveOverlap: 'shiftY' },
      endLabel: { show: !bar, formatter: () => label, color, fontSize: 11, fontFamily: palette.sans, distance: 6, width: Math.max(0, labelWidth - 8), overflow: 'truncate' },
      tooltip: { valueFormatter: (value: number) => formatValue(value, measure.format) },
      ...(panel === 0 && index === 0 ? referenceMarks(block, palette, measure.format) : {}),
      ...(index === firstBelow ? referenceMarks(block, palette, measure.format, 'right') : {}),
      ...valueLabels(block, values, measure.format, palette),
      ...(bar ? {} : curve(block)),
      ...(markOf(measure, index) === 'area' ? { areaStyle: { color, opacity: 0.1 } } : {}),
    };
    const compared = Object.entries(envelope.compare).map(([id, rows]) => {
      const byTime = new Map(rows.map((row) => [String(row.t), row]));
      const name = `${label} · ${id === 'prior' ? 'Prior period' : id === 'ly' ? 'Last year' : 'Two years ago'}`;
      return {
        ...current,
        id: `${measure.name}:${id}`,
        name,
        type: 'line',
        showSymbol: rows.filter((row) => row[measure.name] !== null && row[measure.name] !== undefined).length === 1,
        data: days.map((day) => (byTime.get(day)?.[measure.name] as number | null) ?? null),
        lineStyle: { width: 1.5, color, type: 'dashed', opacity: 0.55 },
        areaStyle: undefined,
        endLabel: { ...current.endLabel, show: false },
        label: { show: false },
        markLine: undefined,
        markPoint: undefined,
      };
    });
    return [current, ...compared];
  });
  const formatOf = (panel: number) => measures.find((measure, index) => (panels === 2 && lower(measure, index) ? 1 : 0) === panel)?.format;
  return {
    animation: false,
    textStyle: { fontFamily: palette.sans, fontSize: 11, color: palette.ink3 },
    axisPointer: { link: [{ xAxisIndex: 'all' }] },
    grid: grids,
    tooltip: { trigger: 'axis', axisPointer: { label: { formatter: (entry: { value: string }) => pointLabel(String(entry.value), block.grain ?? envelope.period.grain) } }, backgroundColor: palette.surface, borderColor: palette.hairlineStrong, textStyle: { color: palette.ink, fontSize: 12, fontFamily: palette.sans }, confine: true },
    xAxis: grids.map((_, panel) => ({
      type: 'category',
      gridIndex: panel,
      data: days,
      boundaryGap: measures.some((measure, index) => isBar(measure, index)),
      axisLine: { lineStyle: { color: palette.hairlineStrong } },
      axisTick: { show: false },
      ...(panel === panels - 1 ? xAxisName(block, palette) : {}),
      axisLabel: { show: panel === panels - 1, color: palette.ink3, fontSize: 11, hideOverlap: true, interval: ((index: number, day: string) => index === 0 || index === days.length - 1 || day.slice(5, 7) !== days[index - 1]?.slice(5, 7)), formatter: time.label ?? monthOf, ...xLabelSpacing(block), ...(time.tick ? { interval: time.tick } : {}) },
    })),
    yAxis: grids.map((_, panel) => ({
      type: 'value',
      gridIndex: panel,
      triggerEvent: true,
      ...(panel === 0 ? scaleBounds(axisScale(block)) : scaleBounds(axisScale({ ...block, scale: block.scale2 }))),
      scale: !measures.some((measure, index) => isBar(measure, index) && (panels === 2 && lower(measure, index) ? 1 : 0) === panel) && !(panel === 0 ? axisScale(block) : axisScale({ ...block, scale: block.scale2 })).zero,
      splitNumber: panels === 2 ? 2 : 4,
      ...(panel === 0 ? yAxisName(block, unitName(formatOf(panel), palette.ink3), palette) : unitName(formatOf(panel), palette.ink3)),
      axisLabel: { color: palette.ink3, fontSize: 11, formatter: (value: number) => formatAxis(value, formatOf(panel)) },
      splitLine: { lineStyle: { color: palette.hairline } },
    })),
    series,
  };
}

export function comboLegend(envelope: Envelope, block: Block, palette: Palette): Array<LegendItem> {
  const measures = envelope.columns.filter((column) => column.role === 'measure');
  const marks = (block.marks ?? {}) as Record<string, string>;
  const overrides = seriesOverrides(block);
  if (measures.length < 2 && !Object.keys(envelope.compare).length) return [];
  return measures.flatMap((measure, index): Array<LegendItem> => {
    if (overrides[measure.name]?.hidden) return [];
    const name = overrides[measure.name]?.label ?? measure.label;
    const color = overrideColor(overrides[measure.name], palette, palette.series[index % palette.series.length]);
    const bar = (marks[measure.name] ?? (index === 0 ? 'bar' : 'line')) === 'bar';
    const comparisons = Object.keys(envelope.compare);
    return [
      ...(bar || comparisons.length ? [{ name, color, mark: bar ? 'square' as const : 'line' as const, part: `series:${measure.name}` }] : []),
      ...comparisons.map((id): LegendItem => ({ name: `${name} · ${id === 'prior' ? 'Prior period' : id === 'ly' ? 'Last year' : 'Two years ago'}`, color, mark: 'dash', part: `series:${measure.name}:${id}` })),
    ];
  });
}

export default function Combo({ block, envelope, palette, width }: ComboProps) {
  const option = useMemo(() => comboOption({ block, envelope, palette, width }), [block, envelope, palette, width]);
  return <Chart option={option} />;
}
